import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";
import { useAuth } from "../App";

const API_ROOT = process.env.REACT_APP_API_URL || "/api";
const PUBLIC_ROOT = API_ROOT.replace(/\/api\/?$/, "");

const EMPTY_LESSON = {
  title: "",
  source_title: "",
  source_author: "",
  category: "Indian Buyer Psychology",
  description: "",
  duration_minutes: 15,
  external_url: "",
  company: "all",
  role_level: "all",
};

const STARTER_IDEAS = [
  "Selling With Trust in the Indian Market",
  "Handling Price Objections Without Discounting",
  "Follow-up Discipline for Field Sales",
  "How to Identify Serious Buyers",
  "Indian Customer Psychology: Respect, Timing, Relationship",
];

function audioSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${PUBLIC_ROOT}${url}`;
}

function pct(done, total) {
  if (!Number(total)) return 0;
  return Math.round((Number(done || 0) / Number(total)) * 100);
}

export default function SalesLearning({ accent = "#E31E24" }) {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_LESSON);
  const [audio, setAudio] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dueDate, setDueDate] = useState("");

  // Restricted admins (non-superadmin admin) can view learning but cannot add lessons
  const crmRole = user?.app_role_overrides?.crm || user?.role;
  const isSuperAdmin = user?.role === "superadmin" || crmRole === "superadmin";
  const canManage = isSuperAdmin || crmRole === "manager";
  const canAdd = isSuperAdmin;
  const isEbekmed = String(user?.company || "").toLowerCase() === "ebekmed";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // Individual .catch() so one failing endpoint doesn't kill the whole section
      const [lessonRes, usersRes, reportRes] = await Promise.all([
        API.get("/sales-learning/lessons").catch(() => null),
        canManage ? API.get("/users").catch(() => null) : Promise.resolve(null),
        canManage ? API.get("/sales-learning/team-report").catch(() => null) : Promise.resolve(null),
      ]);
      if (lessonRes) setLessons(lessonRes.data || []);
      if (canManage) {
        if (usersRes) {
          const list = usersRes.data || [];
          setEmployees(list.filter(e => e.role === "employee" && (user.role !== "manager" || e.manager_id === user.id)));
        }
        if (reportRes) setReport(reportRes.data || []);
      }
      if (!lessonRes) toast.error("Could not load Sales Learning");
    } finally {
      setLoading(false);
    }
  }, [canManage, user?.id, user?.role]);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const set = new Set(lessons.map(l => l.category || "Sales Skills"));
    return ["All", ...Array.from(set)];
  }, [lessons]);
  const [category, setCategory] = useState("All");
  const visibleLessons = category === "All" ? lessons : lessons.filter(l => (l.category || "Sales Skills") === category);

  const updateProgress = async (lesson, status, extra = {}) => {
    try {
      await API.put(`/sales-learning/lessons/${lesson.id}/progress`, { status, ...extra });
      if (status === "completed") toast.success("Lesson marked completed");
      setLessons(list => list.map(item => (
        item.id === lesson.id
          ? {
              ...item,
              progress_status: status,
              last_position_seconds: extra.last_position_seconds ?? item.last_position_seconds,
              completed_at: status === "completed" ? new Date().toISOString() : item.completed_at,
            }
          : item
      )));
      if (status === "completed" && canManage) load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not update lesson");
    }
  };

  const createLesson = async () => {
    if (!form.title.trim()) return toast.error("Lesson title is required");
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => data.append(key, value ?? ""));
      if (audio) data.append("audio", audio);
      await API.post("/sales-learning/lessons", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Learning lesson added");
      setShowCreate(false);
      setForm(EMPTY_LESSON);
      setAudio(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not add lesson");
    }
  };

  const openAssign = (lesson) => {
    setAssigning(lesson);
    setSelectedUsers([]);
    setDueDate("");
  };

  const assignLesson = async () => {
    if (!assigning || selectedUsers.length === 0) return toast.error("Select employees to assign");
    try {
      await API.post(`/sales-learning/lessons/${assigning.id}/assign`, {
        user_ids: selectedUsers,
        due_date: dueDate || null,
      });
      toast.success("Lesson assigned");
      setAssigning(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not assign lesson");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>{isEbekmed ? "Ebekmed Clinical Sales Learning" : "Sales Learning"}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
            {isEbekmed
              ? "Audio lessons for nursing-college and hospital visits, competency-module demonstrations, objections and follow-up."
              : "Short 15 to 20 minute audio lessons for travel time, follow-up discipline and Indian buyer psychology."}
          </div>
        </div>
        {canAdd && (
          <button onClick={() => setShowCreate(true)} style={primary(accent)}>+ Add Lesson</button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 10, marginBottom: 8 }}>
        {categories.map(c => (
          <button key={c} onClick={() => setCategory(c)}
            style={{
              border: "none",
              borderRadius: 999,
              padding: "8px 12px",
              fontWeight: 800,
              fontSize: 12,
              color: category === c ? "#fff" : "#475569",
              background: category === c ? accent : "#f1f5f9",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}>
            {c}
          </button>
        ))}
      </div>

      {isEbekmed && lessons.some(lesson => lesson.company === "ebekmed") && (
        <div style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 11, background: "#ecfeff", border: "1px solid #99f6e4", color: "#115e59", fontSize: 12, fontWeight: 800 }}>
          Ebekmed field collection: {lessons.filter(lesson => lesson.company === "ebekmed").length} clinical sales audio lessons available below.
        </div>
      )}

      {loading ? (
        <div style={emptyBox}>Loading Sales Learning...</div>
      ) : (
        <>
          {canManage && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
              <Stat label="Lessons" value={lessons.length} color={accent} />
              <Stat label="Assigned" value={report.reduce((s, r) => s + Number(r.assigned_count || 0), 0)} color="#6366f1" />
              <Stat label="Completed" value={report.reduce((s, r) => s + Number(r.completed_count || 0), 0)} color="#16a34a" />
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: canManage ? "1.6fr 1fr" : "1fr", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {visibleLessons.length === 0 ? (
                <div style={emptyBox}>No lessons yet. Add the first audio lesson from Manager or Admin login.</div>
              ) : visibleLessons.map(lesson => (
                <LessonCard
                  key={lesson.id}
                  lesson={lesson}
                  accent={accent}
                  canManage={canManage}
                  onAssign={() => openAssign(lesson)}
                  onStart={(seconds) => updateProgress(lesson, "in_progress", { last_position_seconds: seconds || 0 })}
                  onComplete={() => updateProgress(lesson, "completed")}
                />
              ))}
            </div>

            {canManage && (
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, height: "fit-content" }}>
                <div style={{ fontSize: 14, fontWeight: 900, marginBottom: 10 }}>Team Completion</div>
                {report.length === 0 ? <div style={{ fontSize: 12, color: "#94a3b8" }}>No assignments yet.</div> : report.map(row => {
                  const percent = pct(row.completed_count, row.assigned_count);
                  return (
                    <div key={row.user_id} style={{ borderBottom: "1px solid #f1f5f9", padding: "10px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <b style={{ fontSize: 13 }}>{row.name}</b>
                        <span style={{ fontSize: 12, fontWeight: 900, color: accent }}>{percent}%</span>
                      </div>
                      <div style={{ height: 6, background: "#f1f5f9", borderRadius: 999, marginTop: 7, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${percent}%`, background: percent >= 70 ? "#16a34a" : accent }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 5 }}>
                        {row.completed_count || 0} of {row.assigned_count || 0} completed
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {showCreate && (
        <LessonModal
          accent={accent}
          form={form}
          setForm={setForm}
          setAudio={setAudio}
          onClose={() => setShowCreate(false)}
          onSave={createLesson}
          user={user}
        />
      )}

      {assigning && (
        <AssignModal
          accent={accent}
          lesson={assigning}
          employees={employees}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          dueDate={dueDate}
          setDueDate={setDueDate}
          onClose={() => setAssigning(null)}
          onAssign={assignLesson}
        />
      )}
    </div>
  );
}

function LessonCard({ lesson, accent, canManage, onAssign, onStart, onComplete }) {
  const completed = lesson.progress_status === "completed";
  const hasAudio = !!lesson.audio_url;
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a" }}>{lesson.title}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
            {lesson.source_title ? `Inspired by ${lesson.source_title}` : "Internal sales lesson"}
            {lesson.source_author ? ` - ${lesson.source_author}` : ""}
          </div>
        </div>
        <span style={{ background: completed ? "#dcfce7" : "#f1f5f9", color: completed ? "#15803d" : "#475569", borderRadius: 999, padding: "5px 9px", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
          {completed ? "Completed" : `${lesson.duration_minutes || 15} min`}
        </span>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={chip("#eff6ff", "#1d4ed8")}>{lesson.category || "Sales Skills"}</span>
        {lesson.due_date && <span style={chip("#fff7ed", "#c2410c")}>Due {new Date(lesson.due_date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
        {canManage && <span style={chip("#f5f3ff", "#7c3aed")}>{lesson.completed_count || 0}/{lesson.assigned_count || 0} done</span>}
      </div>
      {lesson.description && <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5, marginBottom: 12 }}>{lesson.description}</div>}
      {hasAudio ? (
        <audio controls preload="none" src={audioSrc(lesson.audio_url)}
          onPlay={(e) => {
            if (lesson.progress_status !== "in_progress" && lesson.progress_status !== "completed") {
              onStart(Math.round(e.currentTarget.currentTime || 0));
            }
          }}
          onEnded={onComplete}
          style={{ width: "100%", height: 38, marginBottom: 10 }} />
      ) : lesson.external_url ? (
        <a href={lesson.external_url} target="_blank" rel="noreferrer"
          onClick={() => onStart(0)}
          style={{ display: "inline-flex", marginBottom: 10, color: accent, fontWeight: 900, fontSize: 13 }}>
          Open listening link
        </a>
      ) : (
        <div style={{ background: "#fff7ed", color: "#92400e", borderRadius: 10, padding: 10, fontSize: 12, marginBottom: 10 }}>
          Audio file will be uploaded soon.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={onComplete} style={secondary(completed ? "#16a34a" : accent)}>{completed ? "Completed" : "Mark Completed"}</button>
        {canManage && <button onClick={onAssign} style={secondary("#475569")}>Assign</button>}
      </div>
    </div>
  );
}

function LessonModal({ accent, form, setForm, setAudio, onClose, onSave, user }) {
  const inp = { width: "100%", padding: "10px 12px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none" };
  const lbl = { fontSize: 11, fontWeight: 900, color: "#64748b", marginBottom: 5, display: "block" };
  return (
    <div style={backdrop}>
      <div style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900 }}>Add Sales Audio Lesson</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>Use original 15 to 20 minute summaries, not full copyrighted book audio.</div>
          </div>
          <button onClick={onClose} style={closeBtn}>x</button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <Field label="TITLE" lbl={lbl}><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Selling With Trust in the Indian Market" style={inp} /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="INSPIRED BY BOOK" lbl={lbl}><input value={form.source_title} onChange={e => setForm(f => ({ ...f, source_title: e.target.value }))} placeholder="Sell" style={inp} /></Field>
            <Field label="AUTHOR" lbl={lbl}><input value={form.source_author} onChange={e => setForm(f => ({ ...f, source_author: e.target.value }))} placeholder="Subroto Bagchi" style={inp} /></Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="CATEGORY" lbl={lbl}><input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inp} /></Field>
            <Field label="DURATION MINUTES" lbl={lbl}><input type="number" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} style={inp} /></Field>
          </div>
          <Field label="DESCRIPTION" lbl={lbl}><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Short practical lesson for field sales employees." style={{ ...inp, resize: "vertical" }} /></Field>
          <Field label="AUDIO MP3/WAV" lbl={lbl}><input type="file" accept="audio/*" onChange={e => setAudio(e.target.files?.[0] || null)} style={inp} /></Field>
          <Field label="EXTERNAL LINK (optional)" lbl={lbl}><input value={form.external_url} onChange={e => setForm(f => ({ ...f, external_url: e.target.value }))} placeholder="Podcast or Drive link until MP3 is uploaded" style={inp} /></Field>
          {user?.role === "superadmin" && (
            <Field label="COMPANY" lbl={lbl}>
              <select value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} style={inp}>
                <option value="all">All</option>
                <option value="ebek">Ebek</option>
                <option value="ebekmed">Ebek Med</option>
              </select>
            </Field>
          )}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>Starter lesson ideas</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {STARTER_IDEAS.map(item => <button key={item} onClick={() => setForm(f => ({ ...f, title: item }))} style={ideaBtn}>{item}</button>)}
            </div>
          </div>
        </div>
        <button onClick={onSave} style={{ ...primary(accent), width: "100%", marginTop: 14 }}>Save Lesson</button>
      </div>
    </div>
  );
}

function AssignModal({ accent, lesson, employees, selectedUsers, setSelectedUsers, dueDate, setDueDate, onClose, onAssign }) {
  const allSelected = selectedUsers.length === employees.length;
  const toggle = (id) => {
    setSelectedUsers(list => list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };
  return (
    <div style={backdrop}>
      <div style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900 }}>Assign Lesson</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>{lesson.title}</div>
          </div>
          <button onClick={onClose} style={closeBtn}>x</button>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 900, color: "#64748b" }}>DUE DATE</label>
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
            style={{ width: "100%", marginTop: 5, padding: 10, border: "1.5px solid #e2e8f0", borderRadius: 10, fontFamily: "inherit" }} />
        </div>
        <button onClick={() => setSelectedUsers(allSelected ? [] : employees.map(e => e.id))} style={secondary(accent)}>
          {allSelected ? "Clear All" : "Select All"}
        </button>
        <div style={{ maxHeight: 280, overflowY: "auto", marginTop: 10, border: "1px solid #e2e8f0", borderRadius: 12 }}>
          {employees.map(emp => (
            <label key={emp.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 11, borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}>
              <input type="checkbox" checked={selectedUsers.includes(emp.id)} onChange={() => toggle(emp.id)} />
              <span style={{ fontSize: 13, fontWeight: 800 }}>{emp.name}</span>
              <small style={{ marginLeft: "auto", color: "#94a3b8" }}>{emp.company?.toUpperCase()}</small>
            </label>
          ))}
        </div>
        <button onClick={onAssign} style={{ ...primary(accent), width: "100%", marginTop: 14 }}>Assign to Team</button>
      </div>
    </div>
  );
}

function Field({ label, lbl, children }) {
  return <div><label style={lbl}>{label}</label>{children}</div>;
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 13, textAlign: "center" }}>
      <div style={{ fontSize: 24, fontWeight: 950, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>{label}</div>
    </div>
  );
}

const primary = (accent) => ({
  background: accent,
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer",
  fontFamily: "inherit",
});

const secondary = (color) => ({
  background: `${color}12`,
  color,
  border: `1px solid ${color}30`,
  borderRadius: 9,
  padding: "8px 11px",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
  fontFamily: "inherit",
});

const chip = (bg, color) => ({ background: bg, color, borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900 });
const emptyBox = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 28, textAlign: "center", color: "#64748b", fontSize: 13 };
const backdrop = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.58)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" };
const modal = { background: "#fff", borderRadius: "22px 22px 0 0", padding: 22, width: "100%", maxWidth: 720, maxHeight: "92vh", overflowY: "auto" };
const closeBtn = { border: "none", background: "#f1f5f9", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontWeight: 900 };
const ideaBtn = { border: "none", background: "#eef2ff", color: "#3730a3", borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 800, cursor: "pointer" };
